import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiDownload,
    FiFileText,
    FiRefreshCw,
    FiSearch,
    FiTrash2,
} from 'react-icons/fi';
import {
    executeCheckForecastBulkDeleteImport,
    getCheckForecastBulkDeleteImportPreview,
} from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import {
    formatParisDateTime,
    getAdminEmailFromToken,
    getBulkDeleteErrorMessage,
    getPreviewTotal,
    hasBulkDeletePreviewData,
    isBulkDeleteConfirmValid,
    isHighRiskBulkDelete,
    validateBulkDeleteImportFile,
    downloadBulkDeleteImportTemplate,
    BULK_DELETE_IMPORT_ACCEPT,
} from '../../utils/checkForecastBulkDelete';
import { useSelector } from 'react-redux';

const PAGE_PREFIX = 'pages.adminTripsForecastBackupCheck.bulkDelete';
const bulkDeleteKey = (path) => PAGE_PREFIX + '.' + path;

const CheckForecastBulkDeleteFileTab = () => {
    const { t } = useTranslation();
    const tb = (path, params) => t(bulkDeleteKey(path), params);
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const userEmail = getAdminEmailFromToken(accessToken);

    const [importFile, setImportFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewFileName, setPreviewFileName] = useState('');
    const [deleteResult, setDeleteResult] = useState(null);
    const [deletedAt, setDeletedAt] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [fileError, setFileError] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmChecked, setConfirmChecked] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    const fileInputRef = useRef(null);
    const confirmInputRef = useRef(null);
    const requestVersionRef = useRef(0);
    const abortRef = useRef(null);

    useEffect(() => () => abortRef.current?.abort(), []);

    useEffect(() => {
        if (!deleteLoading) return undefined;
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [deleteLoading]);

    useEffect(() => {
        if (!showConfirm) return;
        const timer = window.setTimeout(() => confirmInputRef.current?.focus(), 100);
        return () => window.clearTimeout(timer);
    }, [showConfirm]);

    const errorLabels = {
        invalidDate: tb('errors.invalidDate'),
        unauthorized: tb('errors.unauthorized'),
        forbidden: tb('errors.forbidden'),
        server: tb('errors.server'),
        default: tb('errors.default'),
    };

    const invalidateRequest = () => {
        requestVersionRef.current += 1;
        abortRef.current?.abort();
        abortRef.current = null;
    };

    const resetPreviewState = () => {
        setPreview(null);
        setPreviewFileName('');
        setDeleteResult(null);
        setDeletedAt(null);
        setPreviewError(null);
        setDeleteError(null);
        setShowConfirm(false);
        setConfirmChecked(false);
        setConfirmText('');
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0] || null;
        invalidateRequest();
        setPreviewLoading(false);
        setDeleteLoading(false);
        setImportFile(file);
        setFileError(file ? validateBulkDeleteImportFile(file) : null);
        resetPreviewState();
    };

    const handlePreview = async (event) => {
        event?.preventDefault();
        const validationError = validateBulkDeleteImportFile(importFile);
        if (validationError) {
            setFileError(validationError);
            return;
        }

        invalidateRequest();
        const version = requestVersionRef.current;
        const controller = new AbortController();
        abortRef.current = controller;

        setPreviewLoading(true);
        setPreviewError(null);
        setDeleteError(null);
        resetPreviewState();

        try {
            const response = await getCheckForecastBulkDeleteImportPreview(importFile, controller.signal);
            if (version !== requestVersionRef.current) return;
            setPreview(response);
            setPreviewFileName(importFile.name);
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            if (version !== requestVersionRef.current) return;
            setPreviewError(getBulkDeleteErrorMessage(err, errorLabels));
        } finally {
            if (version === requestVersionRef.current) {
                setPreviewLoading(false);
                abortRef.current = null;
            }
        }
    };

    const previewTotal = getPreviewTotal(preview);
    const isHighRisk = isHighRiskBulkDelete(previewTotal);
    const isConfirmValid = isBulkDeleteConfirmValid({
        confirmChecked,
        confirmText,
        tripDay: 'DELETE',
        total: previewTotal,
    });
    const previewReady = Boolean(preview && previewFileName === importFile?.name && !previewLoading);
    const previewHasData = previewReady && hasBulkDeletePreviewData(preview);
    const previewRows = Array.isArray(preview?.rows) ? preview.rows : [];
    const isBusy = previewLoading || deleteLoading;

    const handleConfirmDelete = async () => {
        if (!isConfirmValid || !importFile || !hasBulkDeletePreviewData(preview)) return;

        invalidateRequest();
        const version = requestVersionRef.current;
        const controller = new AbortController();
        abortRef.current = controller;

        setDeleteLoading(true);
        setDeleteError(null);

        try {
            const response = await executeCheckForecastBulkDeleteImport(importFile, controller.signal);
            if (version !== requestVersionRef.current) return;

            if (response?.success === false) {
                setDeleteError(response.message || tb('errors.confirmRequired'));
                return;
            }

            setDeleteResult(response);
            setDeletedAt(new Date());
            setPreview(null);
            setPreviewFileName('');
            setShowConfirm(false);
            setConfirmChecked(false);
            setConfirmText('');
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            if (version !== requestVersionRef.current) return;
            setDeleteError(getBulkDeleteErrorMessage(err, errorLabels));
        } finally {
            if (version === requestVersionRef.current) {
                setDeleteLoading(false);
                abortRef.current = null;
            }
        }
    };

    return (
        <div className="cfbd-mode-panel">
            <section className="cfbd-section cfbd-section--form" aria-labelledby="cfbd-file-section-title">
                <div className="cfbd-mode-header">
                    <h4 id="cfbd-file-section-title" className="cfbd-section__title">
                        {tb('modes.byFile.title')}
                    </h4>
                    <p className="cfbd-mode-desc">{tb('modes.byFile.description')}</p>
                </div>

                <Form onSubmit={handlePreview} className="cfbd-form">
                    <div className="cfbd-form-toolbar cfbd-form-toolbar--file">
                        <Form.Group className="cfbd-form-field cfbd-form-field--file">
                            <Form.Label htmlFor="cfbd-import-file">
                                {tb('modes.byFile.fileLabel')}
                            </Form.Label>
                            <Form.Control
                                ref={fileInputRef}
                                id="cfbd-import-file"
                                type="file"
                                accept={BULK_DELETE_IMPORT_ACCEPT}
                                onChange={handleFileChange}
                                disabled={isBusy}
                                aria-describedby="cfbd-file-form-hint"
                            />
                        </Form.Group>
                        <div className="cfbd-form-action cfbd-form-action--stacked">
                            <Button
                                type="button"
                                variant="outline-secondary"
                                size="sm"
                                className="cfbd-form-secondary-btn"
                                onClick={downloadBulkDeleteImportTemplate}
                            >
                                <FiDownload size={14} className="me-1" aria-hidden />
                                {tb('modes.byFile.downloadTemplate')}
                            </Button>
                            <Button
                                type="submit"
                                className="btn fb-btn-primary cfbd-btn-primary"
                                disabled={isBusy || !importFile || Boolean(fileError)}
                                aria-busy={previewLoading}
                            >
                                {previewLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" aria-hidden />
                                        {tb('previewing')}
                                    </>
                                ) : (
                                    <>
                                        <FiSearch size={16} className="me-2" aria-hidden />
                                        {tb('modes.byFile.previewButton')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                    <p id="cfbd-file-form-hint" className="cfbd-form-hint">
                        {tb('modes.byFile.fileHint')}
                        {importFile && !fileError && (
                            <>
                                {' · '}
                                <FiFileText className="cfbd-form-hint__icon" aria-hidden />
                                {importFile.name}
                            </>
                        )}
                    </p>
                    {fileError && (
                        <p className="cfbd-form-error" role="alert">
                            {tb('modes.byFile.errors.' + fileError)}
                        </p>
                    )}
                </Form>
            </section>

            {previewError && (
                <Alert variant="danger" role="alert" dismissible onClose={() => setPreviewError(null)}>
                    {previewError}
                </Alert>
            )}

            {deleteError && (
                <Alert variant="danger" role="alert" dismissible onClose={() => setDeleteError(null)}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>{deleteError}</span>
                        {previewHasData && (
                            <Button variant="outline-danger" size="sm" onClick={() => setShowConfirm(true)} disabled={isBusy}>
                                <FiRefreshCw size={14} className="me-1" aria-hidden />
                                {tb('retryDelete')}
                            </Button>
                        )}
                    </div>
                </Alert>
            )}

            {previewReady && (
                <Card className="cfbd-preview-card mb-3">
                    <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <strong>{tb('previewTitle')}</strong>
                        <Badge bg="secondary">{previewFileName}</Badge>
                    </Card.Header>
                    <Card.Body>
                        {preview?.message && <p className="text-muted mb-3">{preview.message}</p>}
                        <div className="cfbd-impact-total mb-3" role="status">
                            <span className="cfbd-impact-total__label">{tb('counts.total_records_to_delete')}</span>
                            <span className="cfbd-impact-total__value">{previewTotal}</span>
                        </div>

                        {previewRows.length > 0 && (
                            <div className="table-responsive mb-3">
                                <Table size="sm" striped bordered hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>{tb('tripDayLabel')}</th>
                                            <th>{tb('policyCodeLabel')}</th>
                                            <th>{tb('modes.byFile.colRecords')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, index) => (
                                            <tr key={row.id || row.policy_code + row.trip_day + index}>
                                                <td>{row.trip_day || '—'}</td>
                                                <td><code>{row.policy_code || '—'}</code></td>
                                                <td>{row.total_records_to_delete ?? row.records_count ?? '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {previewHasData && (
                            <Button variant="danger" onClick={() => setShowConfirm(true)} disabled={isBusy}>
                                <FiTrash2 size={16} className="me-2" aria-hidden />
                                {tb('modes.byFile.deleteButton')}
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            )}

            {deleteResult?.success && (
                <Alert variant="success" role="status">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <FiCheckCircle aria-hidden />
                        <strong>{deleteResult.message || tb('successTitle')}</strong>
                    </div>
                    <p className="mb-1">
                        <strong>{tb('auditLog.user')} :</strong> {userEmail}
                    </p>
                    <p className="mb-0">
                        <strong>{tb('auditLog.deletedAt')} :</strong>{' '}
                        {deletedAt ? formatParisDateTime(deletedAt) : '—'}
                    </p>
                    <Button
                        variant="outline-success"
                        size="sm"
                        className="mt-3 cfbd-btn-full-mobile"
                        onClick={() => {
                            setDeleteResult(null);
                            setDeletedAt(null);
                            setImportFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                            resetPreviewState();
                        }}
                    >
                        {tb('modes.byFile.analyzeAnother')}
                    </Button>
                </Alert>
            )}

            <Modal
                show={showConfirm}
                onHide={() => !deleteLoading && setShowConfirm(false)}
                backdrop={deleteLoading ? 'static' : true}
                keyboard={!deleteLoading}
                centered
            >
                <Modal.Header closeButton={!deleteLoading}>
                    <Modal.Title className="text-danger">
                        <FiAlertTriangle className="me-2" aria-hidden />
                        {tb('confirmTitle')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{tb('modes.byFile.confirmMessage', { total: previewTotal, file: previewFileName })}</p>
                    <Form.Check
                        type="checkbox"
                        id="cfbd-file-confirm-checkbox"
                        className="mb-3"
                        checked={confirmChecked}
                        disabled={deleteLoading}
                        onChange={(e) => setConfirmChecked(e.target.checked)}
                        label={tb('confirmCheckbox')}
                    />
                    {isHighRisk && (
                        <Form.Group>
                            <Form.Label htmlFor="cfbd-file-confirm-text">{tb('confirmTextLabel')}</Form.Label>
                            <Form.Control
                                ref={confirmInputRef}
                                id="cfbd-file-confirm-text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                disabled={deleteLoading}
                                placeholder="DELETE"
                            />
                            <Form.Text className="text-muted">{tb('confirmTextHint', { threshold: 1000 })}</Form.Text>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowConfirm(false)} disabled={deleteLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteLoading || !isConfirmValid}>
                        {deleteLoading ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" aria-hidden />
                                {tb('deleting')}
                            </>
                        ) : (
                            tb('modes.byFile.confirmDelete')
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CheckForecastBulkDeleteFileTab;
